import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, LogOut, NotebookText, SkipForward } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { PizzaTimerIntegracaoProps } from "../types";
import PedidoOriginalModal from "../../../components/PedidoOriginalModal";
import Modal from "../../../components/Modal";
import { cardapioService } from "../../../services/cardapioService";
import { CardapioItem } from "../../../types/cardapio";
import {
  buildCardapioSaboresMap,
  removeSaborFractionPrefix,
  resolveCardapioSabor,
} from "../../../utils/resolveCardapioSabor";
import {
  filaTipoLabel,
  isFilaRodizioTipo,
  isFilaSalaoTipo,
  isFilaSalgadaFamilia,
} from "../../../services/filaPizzasService";
import {
  dedupeObservacoes,
  resolveSalaoProducaoLabels,
} from "../../Painel/utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const resolveFotoUrl = (fotoUrl?: string | null): string | null => {
  if (!fotoUrl) return null;
  if (/^https?:\/\//i.test(fotoUrl)) return fotoUrl;
  const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
  return `${apiBaseUrl}${fotoUrl}`;
};

const PizzaTimerIntegracao: React.FC<PizzaTimerIntegracaoProps> = ({
  item,
  timeElapsed,
  isFinalizing,
  isPulando = false,
  isDefinindoTamanhoRodizio = false,
  onFinalizarProximo,
  onFinalizarOffline,
  onPularItem,
  onDefinirTamanhoRodizio,
}) => {
  const [showPedidoModal, setShowPedidoModal] = useState(false);
  const [showPularModal, setShowPularModal] = useState(false);
  const [selectedSabor, setSelectedSabor] = useState<{
    nomeNoPedido: string;
    cadastro: CardapioItem;
  } | null>(null);
  const [expandedFoto, setExpandedFoto] = useState<{
    nome: string;
    url: string;
  } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedSoundRef = useRef(false);
  const { data: saboresCardapio = [] } = useQuery({
    queryKey: ["cardapio-sabores-integracao"],
    queryFn: () => cardapioService.getSabores(),
    staleTime: 60_000,
  });

  const saboresCardapioMap = useMemo(
    () => buildCardapioSaboresMap(saboresCardapio),
    [saboresCardapio]
  );

  const desc = item.descricao?.[0];
  const pizzaName = desc?.name || "Pizza";
  const bordaRaw = desc?.borda as unknown;
  const borda =
    typeof bordaRaw === "string"
      ? bordaRaw.trim()
      : bordaRaw != null && typeof bordaRaw === "object"
        ? String(
            (bordaRaw as { name?: string; nome?: string }).name ||
              (bordaRaw as { name?: string; nome?: string }).nome ||
              ""
          ).trim()
        : "";
  // Só a borda desta pizza: não herdar a borda de outra pizza da mesma fila
  // (ex.: DobroMingo, onde a 2ª pizza vai sem borda).
  const bordaLabel = borda;
  const sabores = desc?.sabores || [];
  const acrescimos = Array.isArray(desc?.acrescimos) ? desc.acrescimos : [];
  const totalSabores = sabores.length;
  const displayId = desc?.display_id || item.displayId;
  const itemOriginalName = desc?.item_original_name;
  const isSalao = isFilaSalaoTipo(item.tipo);
  const isRodizio = isFilaRodizioTipo(item.tipo);
  const precisaSelecionarTamanhoRodizio =
    isRodizio &&
    !(
      desc &&
      typeof desc === "object" &&
      (desc as { tamanhoSelecionado?: boolean }).tamanhoSelecionado === true
    );
  const isSalgadaFamilia = isFilaSalgadaFamilia(item.tipo);
  const salaoLabels = isSalao
    ? resolveSalaoProducaoLabels({
        mesaId: item.pedidoFoodfly?.mesaId,
        comandaId: item.pedidoFoodfly?.comandaId,
        identificador: item.pedidoFoodfly?.identificador,
        displayId,
      })
    : null;
  const customerName = isSalao
    ? salaoLabels?.cliente
    : item.pedidoIntegracao?.customerName;
  const tipoLabel = filaTipoLabel(item.tipo);
  const filaBadgeLabel =
    isSalao || isRodizio
      ? isSalgadaFamilia
        ? "Salgada"
        : "Doce"
      : `Fila ${tipoLabel}`;

  const observacoes = dedupeObservacoes(
    isSalao ? item.pedidoFoodfly?.observacao : item.pedidoIntegracao?.observation,
    !isSalao
      ? (
          item.pedidoIntegracao as
            | { internalObservation?: string | null }
            | undefined
        )?.internalObservation
      : null,
    item.observation,
    ...(Array.isArray(item.descricao)
      ? item.descricao.map((d: { observacao?: string | null }) => d?.observacao)
      : item.descricao && typeof item.descricao === "object"
        ? [(item.descricao as { observacao?: string | null }).observacao]
        : [])
  );
  const observationPulseClass =
    "border-error-dark bg-error-light text-error-text shadow-[0_0_0_0_rgb(220_38_38/55%)] animate-forno-observation-pulse";

  useEffect(() => {
    audioRef.current = new Audio("/producao.mp3");
    audioRef.current.volume = 1.0;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    hasPlayedSoundRef.current = false;
  }, [item.id]);

  useEffect(() => {
    if (isFinalizing || hasPlayedSoundRef.current || !audioRef.current) return;

    const isTrem =
      pizzaName.toLowerCase().includes("trem") ||
      (itemOriginalName || "").toLowerCase().includes("trem");
    const timeLimit = isTrem ? 120 : 90;

    if (timeElapsed >= timeLimit && !hasPlayedSoundRef.current) {
      hasPlayedSoundRef.current = true;
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.error("Erro ao reproduzir o som:", error);
      });
    }
  }, [timeElapsed, pizzaName, itemOriginalName, isFinalizing]);

  return (
    <div
      className={`animate-fadeInUp relative mx-auto mb-4 max-w-md overflow-hidden rounded-3xl border-4 p-3 text-left shadow-2xl sm:p-4 ${
        isSalao
          ? "border-[#5c0000] bg-[#8B0000]"
          : isRodizio
            ? "border-amber-500 bg-gradient-to-br from-amber-50 via-white to-orange-50"
            : "border-primary bg-gradient-to-br from-white via-red-50 to-orange-50"
      }`}
    >
      <div className="absolute left-3 top-3 z-20 flex flex-nowrap items-center gap-1.5">
        {isSalao && (
          <span className="inline-flex rounded-full border border-emerald-400 bg-emerald-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
            Salão
          </span>
        )}
        {isRodizio && (
          <span className="inline-flex rounded-full bg-amber-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
            Rodízio
          </span>
        )}
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-bold shadow-sm ${
            isSalgadaFamilia
              ? "bg-orange-100 text-orange-700"
              : "bg-pink-100 text-pink-700"
          }`}
        >
          {filaBadgeLabel}
        </span>
      </div>

      <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowPularModal(true)}
          disabled={isFinalizing || isPulando}
          className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-amber-500 bg-amber-50 text-amber-700 shadow-md transition hover:bg-amber-100 disabled:opacity-50"
          title="Pular pedido"
        >
          <SkipForward size={16} />
        </button>
        {!isSalao && !isRodizio && (
          <button
            type="button"
            onClick={() => setShowPedidoModal(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-md transition hover:bg-primary-dark"
            title="Ver pedido original"
          >
            <NotebookText size={16} />
          </button>
        )}
      </div>

      <div className="relative z-10 mb-3 min-w-0 pt-9 text-left">
        <div className="rounded-xl bg-white/80 px-3 py-2 backdrop-blur-sm">
          <div className="mb-1.5">
            {itemOriginalName && itemOriginalName !== pizzaName && (
              <div className="mb-1.5 text-xs text-neutral-500">
                {itemOriginalName}
              </div>
            )}
            <SalaoProducaoOrderHeader
              labels={salaoLabels}
              fallbackBadge={
                isSalao ? undefined : `Pedido #${displayId}`
              }
              customerName={customerName}
            />
          </div>

          {observacoes.length > 0 && (
            <div
              className={`mb-3 rounded-lg border p-2 text-left transition-colors duration-300 ${observationPulseClass}`}
            >
              <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-error-dark">
                Obs
              </div>
              <div className="text-xs font-extrabold uppercase leading-relaxed tracking-wide text-error-text sm:text-sm">
                {observacoes.join(" | ")}
              </div>
            </div>
          )}

          <div className="mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              Tamanho
            </span>
            {precisaSelecionarTamanhoRodizio ? (
              <div className="mt-1.5 space-y-2">
                <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                  Selecione o tamanho antes de montar:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={isDefinindoTamanhoRodizio || isFinalizing}
                    onClick={() => onDefinirTamanhoRodizio?.("08")}
                    className="rounded-xl border-2 border-primary bg-primary px-3 py-3 text-sm font-extrabold uppercase tracking-wide text-white shadow-md transition hover:bg-primary/90 disabled:opacity-60"
                  >
                    08 pedaços
                  </button>
                  <button
                    type="button"
                    disabled={isDefinindoTamanhoRodizio || isFinalizing}
                    onClick={() => onDefinirTamanhoRodizio?.("broto")}
                    className="rounded-xl border-2 border-amber-600 bg-amber-600 px-3 py-3 text-sm font-extrabold uppercase tracking-wide text-white shadow-md transition hover:bg-amber-700 disabled:opacity-60"
                  >
                    Broto
                  </button>
                </div>
                {isDefinindoTamanhoRodizio && (
                  <p className="text-center text-xs text-neutral-500">
                    Salvando tamanho...
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-1.5 rounded-xl border-2 border-primary/40 bg-primary px-3 py-2 shadow-md">
                <span className="block text-base font-extrabold uppercase leading-snug tracking-wide text-white sm:text-lg">
                  {pizzaName}
                </span>
              </div>
            )}
          </div>

          <div className="mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              Borda
            </span>
            <div className="mt-1.5 rounded-xl border-2 border-neutral-300 bg-neutral-700 px-3 py-2 shadow-md">
              <span className="block text-left text-base font-extrabold uppercase leading-snug tracking-wide text-white sm:text-lg">
                {bordaLabel || "Sem borda"}
              </span>
            </div>
          </div>

          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              Sabores
            </span>
            <div className="mt-1.5 flex flex-col gap-1.5">
              {sabores.map((sabor: string, idx: number) =>
                (() => {
                  const saborSemFracao = removeSaborFractionPrefix(sabor);
                  const cadastro = resolveCardapioSabor(
                    saborSemFracao,
                    saboresCardapio,
                    saboresCardapioMap
                  );
                  const saborLabel = `${
                    totalSabores > 0 ? `1/${totalSabores} ` : ""
                  }${saborSemFracao || sabor}`;

                  const badgeBase =
                    "w-full rounded-xl border-2 px-3 py-2 text-left shadow-md";
                  const textBase =
                    "block break-words text-base font-extrabold uppercase leading-snug tracking-wide text-white sm:text-lg";

                  if (cadastro) {
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() =>
                          setSelectedSabor({
                            nomeNoPedido: sabor,
                            cadastro,
                          })
                        }
                        className={`${badgeBase} border-emerald-700/60 bg-emerald-800 transition hover:bg-emerald-900`}
                        title="Ver ingredientes cadastrados"
                      >
                        <span className={textBase}>{saborLabel}</span>
                      </button>
                    );
                  }

                  return (
                    <div
                      key={idx}
                      className={`${badgeBase} border-emerald-600/50 bg-emerald-700`}
                    >
                      <span className={textBase}>{saborLabel}</span>
                    </div>
                  );
                })()
              )}
              {sabores.length === 0 && (
                <div className="rounded-xl border-2 border-neutral-200 bg-neutral-100 px-3 py-2 text-xs font-medium text-neutral-500 shadow-sm">
                  Sem sabores informados
                </div>
              )}
            </div>
          </div>

          {acrescimos.length > 0 && (
            <div className="mt-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                Acréscimos
              </span>
              <div className="mt-1.5 flex flex-col gap-1.5">
                {acrescimos.map((acrescimo, idx) => (
                  <div
                    key={`acrescimo-${idx}`}
                    className="w-full rounded-xl border-2 border-amber-700/60 bg-amber-800 px-3 py-2 text-left shadow-md"
                  >
                    <span className="block break-words text-base font-extrabold uppercase leading-snug tracking-wide text-white sm:text-lg">
                      {acrescimo}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="relative z-10 mb-3 text-center">
        <div className="rounded-2xl border-2 border-red-100 bg-white/80 p-2 shadow-inner backdrop-blur-sm">
          <div className="bg-gradient-to-r from-primary via-red-600 to-orange-600 bg-clip-text font-mono text-3xl font-bold tracking-wider text-transparent drop-shadow-lg sm:text-4xl">
            {formatTime(timeElapsed)}
          </div>
          <div className="mt-0.5 text-[11px] font-medium text-neutral-600">
            Tempo decorrido
          </div>
        </div>
      </div>

      <div className="relative z-10 flex flex-col gap-1.5">
        <button
          type="button"
          onClick={onFinalizarProximo}
          disabled={
            isFinalizing ||
            precisaSelecionarTamanhoRodizio ||
            isDefinindoTamanhoRodizio
          }
          title={
            precisaSelecionarTamanhoRodizio
              ? "Selecione o tamanho (08 ou broto) antes de finalizar"
              : undefined
          }
          className="w-full rounded-xl border border-green-400 bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex items-center justify-center gap-2">
            <CheckCircle size={16} />
            {isFinalizing ? "Finalizando..." : "Finalizar e Próximo"}
          </span>
        </button>
        <button
          type="button"
          onClick={onFinalizarOffline}
          disabled={
            isFinalizing ||
            precisaSelecionarTamanhoRodizio ||
            isDefinindoTamanhoRodizio
          }
          title={
            precisaSelecionarTamanhoRodizio
              ? "Selecione o tamanho (08 ou broto) antes de finalizar"
              : undefined
          }
          className="w-full rounded-xl border border-orange-300 bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex items-center justify-center gap-2">
            <LogOut size={16} />
            {isFinalizing ? "Finalizando..." : "Finalizar e Parar"}
          </span>
        </button>
      </div>

      {!isSalao && !isRodizio && (
        <PedidoOriginalModal
          orderId={item.orderId}
          isOpen={showPedidoModal}
          onClose={() => setShowPedidoModal(false)}
        />
      )}

      <Modal
        isOpen={showPularModal}
        onClose={() => setShowPularModal(false)}
        title="Pular pedido"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Selecione o motivo para pular este pedido. O bordeiro será avisado
            da borda em falta.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                onPularItem("sem_borda");
                setShowPularModal(false);
              }}
              className="rounded-lg border-2 border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
            >
              Sem borda
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowPularModal(false)}
              className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!selectedSabor}
        onClose={() => {
          setSelectedSabor(null);
          setExpandedFoto(null);
        }}
        title={
          selectedSabor
            ? `Ingredientes - ${selectedSabor.nomeNoPedido}`
            : "Ingredientes"
        }
        key={
          selectedSabor
            ? `sabor-ingredientes-${selectedSabor.cadastro.id}-${selectedSabor.nomeNoPedido}`
            : "sabor-ingredientes-fechado"
        }
      >
        {selectedSabor && (
          <div>
            {(() => {
              const fotoSaborUrl = resolveFotoUrl(
                selectedSabor.cadastro.fotoUrl
              );
              if (!fotoSaborUrl) return null;
              return (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedFoto({
                      nome: selectedSabor.cadastro.nome,
                      url: fotoSaborUrl,
                    })
                  }
                  className="mb-3 block w-full overflow-hidden rounded-xl border border-neutral-200 bg-white text-left transition hover:opacity-95"
                  title="Clique para ampliar a imagem"
                >
                  <img
                    src={fotoSaborUrl}
                    alt={`Foto do sabor ${selectedSabor.cadastro.nome}`}
                    className="h-40 w-full object-cover"
                    loading="lazy"
                  />
                </button>
              );
            })()}
            <p className="mb-3 text-sm text-neutral-600">
              Sabor cadastrado como{" "}
              <span className="font-semibold">
                {selectedSabor.cadastro.nome}
              </span>
              .
            </p>
            <ul className="space-y-2">
              {selectedSabor.cadastro.ingredientes.map((ingrediente, index) => (
                <li
                  key={`${ingrediente}-${index}`}
                  className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700"
                >
                  {ingrediente}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!expandedFoto}
        onClose={() => setExpandedFoto(null)}
        title={expandedFoto ? `Foto - ${expandedFoto.nome}` : "Foto do sabor"}
      >
        {expandedFoto && (
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <img
              src={expandedFoto.url}
              alt={`Foto ampliada do sabor ${expandedFoto.nome}`}
              className="max-h-[75vh] w-full object-contain"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PizzaTimerIntegracao;
